import React, { useState } from "react";
import api from "@/lib/api";

const prompts = {
  invest: [
    "What matters most for this investment?",
    "Explain the rent and yield evidence.",
    "What should I verify before making an offer?",
  ],
  live_in: [
    "What matters most for living here?",
    "Which location checks are still missing?",
    "What should I verify before making an offer?",
  ],
  own_review: [
    "What should I review as the owner?",
    "Which property risks are still unassessed?",
    "What evidence should I update next?",
  ],
};

export function ReportAssistantPreview() {
  return (
    <section className="report-assistant assistant-preview" aria-labelledby="prop-ai-preview-heading">
      <div className="assistant-status-row">
        <span className="eyebrow">Prop AI analyst</span>
        <span className="assistant-status">Activates with your report</span>
      </div>
      <div className="assistant-preview-grid">
        <div>
          <h2 id="prop-ai-preview-heading">Ask the property, not the paperwork.</h2>
          <p>
            Prepare the report, then ask Prop AI to compare findings, explain
            trade-offs and identify what still needs checking for your goal.
          </p>
        </div>
        <div className="assistant-demo" aria-label="Example Prop AI conversation">
          <p className="assistant-bubble assistant-bubble-user">
            What should I verify before making an offer?
          </p>
          <p className="assistant-bubble assistant-bubble-ai">
            I’ll review the available rent, exposure, planning and locality
            evidence—then separate confirmed findings from missing checks.
          </p>
        </div>
      </div>
      <a className="button assistant-activate" href="#property-details">
        Complete the details to activate Prop AI ↑
      </a>
    </section>
  );
}

export default function ReportAssistant({ goal, metrics }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function ask(event, suggestedQuestion) {
    event?.preventDefault();
    const nextQuestion = (suggestedQuestion || question).trim();
    if (nextQuestion.length < 3 || busy) return;
    setQuestion(nextQuestion);
    setBusy(true);
    setError("");
    setAnswer(null);
    try {
      const { data } = await api.post("/research/assistant", {
        question: nextQuestion,
        goal,
        metrics,
      });
      setAnswer(data);
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : "Prop AI could not analyse this report right now. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="report-assistant" id="ask-prop-ai" aria-labelledby="prop-ai-heading">
      <div className="assistant-status-row">
        <span className="eyebrow">Prop AI analyst</span>
        <span className="assistant-status assistant-status-live">Online · report evidence only</span>
      </div>
      <div className="assistant-intro">
        <div>
          <h2 id="prop-ai-heading">Ask Prop AI about this report.</h2>
        </div>
        <p>
          Prop AI can compare the findings, explain what they mean for your goal,
          and identify the next checks. It can only use the evidence shown here.
        </p>
      </div>
      <div className="assistant-prompts" aria-label="Suggested questions">
        {(prompts[goal] || prompts.invest).map((prompt) => (
          <button key={prompt} type="button" onClick={(event) => ask(event, prompt)} disabled={busy}>
            {prompt}
          </button>
        ))}
      </div>
      <form className="assistant-form" onSubmit={ask}>
        <label htmlFor="report-question">Ask a question about the evidence</label>
        <div>
          <input
            id="report-question"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            minLength={3}
            maxLength={500}
            placeholder="For example: what are the biggest gaps?"
          />
          <button disabled={busy || question.trim().length < 3}>
            {busy ? "Analysing…" : "Ask Prop AI →"}
          </button>
        </div>
      </form>
      <p className="assistant-privacy">
        The street address is not sent to the AI model. Your question and the
        report metrics are processed to prepare this answer.
      </p>
      {error && <p className="error-state" role="alert">{error}</p>}
      {busy && (
        <div className="assistant-conversation" aria-live="polite">
          <p className="assistant-bubble assistant-bubble-user">{question}</p>
          <p className="assistant-bubble assistant-bubble-ai assistant-thinking">
            Reviewing the report evidence…
          </p>
        </div>
      )}
      {answer && (
        <div className="assistant-conversation" aria-live="polite">
          <p className="assistant-bubble assistant-bubble-user">{question}</p>
          <article className="assistant-answer assistant-bubble assistant-bubble-ai">
            <span className="eyebrow">Prop AI analysis</span>
            <p className="assistant-answer-text">{answer.answer}</p>
            {answer.citations?.length > 0 && (
              <div className="assistant-citations">
                <h3>Evidence used</h3>
                <ul>
                  {answer.citations.map((citation) => (
                    <li key={citation.metric_id}>
                      <a href="#evidence">{citation.label}</a>
                      <span>
                        {citation.source_name || "Report input"}
                        {citation.observation_period ? ` · ${citation.observation_period}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {answer.gaps?.length > 0 && (
              <div className="assistant-gaps">
                <h3>Still to verify</h3>
                <ul>{answer.gaps.map((gap) => <li key={gap}>{gap}</li>)}</ul>
              </div>
            )}
            <p className="source">{answer.disclaimer}</p>
          </article>
        </div>
      )}
    </section>
  );
}
